import cx from "clsx"
import { Card, ComponentState, Flex, Group, Icon, Number, Spinner, Text, Title } from "components/common"
import { useGetSalesPlayBookClientProfilingQuery, useGetSalesPlayBookClientSummaryQuery } from "features/leads/api"
import { useMemo, useRef } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import {
  capitalizeFirstLetter,
  convertToSpecificAreaUnit,
  getAreaUnit,
  localisedValue,
  convertToReadableString,
} from "utils"
import theme from "utils/themeVars"
import InfoTable from "./InfoTable"
import styles from "./SalesPlaybook.module.less"

const getLocalizedName = obj => {
  if (!obj) return null
  if (typeof obj === "string") return obj
  const localizedName = localisedValue({ object: obj, key: "name" })
  if (localizedName && typeof localizedName === "string") return localizedName
  if (obj?.name && typeof obj.name === "string") return obj.name
  return null
}

const transformBuyingPreferences = (buyingPreferences, t, userPlatformKey) => {
  if (!buyingPreferences || typeof buyingPreferences !== "object") return []
  const { budgetAndFinancing, propertyPreferences, locationPreferences } = buyingPreferences
  const clientBudget = budgetAndFinancing?.clientBudget
  const financingType = convertToReadableString(budgetAndFinancing?.financingType) || "-"
  const propertyTypeName = capitalizeFirstLetter(getLocalizedName(propertyPreferences?.interestedPropertyType)) || "-"
  const propertySize = propertyPreferences?.interestedPropertySize
  const areaUnit = getAreaUnit(userPlatformKey)
  const targetUnit = userPlatformKey === "bayut_ksa" ? "sq_m" : "sq_f"
  const convertedMin = propertySize?.min ? Math.round(convertToSpecificAreaUnit(propertySize.min, targetUnit)) : 0
  const convertedMax = propertySize?.max ? Math.round(convertToSpecificAreaUnit(propertySize.max, targetUnit)) : ""
  const propertySizeDisplay =
    propertySize?.max || propertySize?.min ? `${convertedMin} - ${convertedMax} ${areaUnit}`.trim() : "-"
  const preferredCityName = capitalizeFirstLetter(getLocalizedName(locationPreferences?.preferredCity)) || "-"
  const preferredLocationName = capitalizeFirstLetter(getLocalizedName(locationPreferences?.preferredLocation)) || "-"

  return [
    { label: t("Budget"), value: clientBudget ? <Number value={clientBudget} isCurrency isStringOnly /> : "-" },
    { label: t("Financing Type"), value: <Text>{financingType}</Text> },
    { label: t("Property Type"), value: <Text>{propertyTypeName}</Text> },
    { label: t("Size"), value: <Text>{propertySizeDisplay}</Text> },
    { label: t("City"), value: <Text>{preferredCityName}</Text> },
    { label: t("Location"), value: <Text>{preferredLocationName}</Text> },
  ]
}

const transformClientProfileData = (clientProfileData, t) => {
  if (!clientProfileData || typeof clientProfileData !== "object") return []
  return [
    {
      label: t("Occupation"),
      value: <Text>{capitalizeFirstLetter(getLocalizedName(clientProfileData?.occupation)) || "-"}</Text>,
    },
    {
      label: t("Gender"),
      value: <Text>{capitalizeFirstLetter(getLocalizedName(clientProfileData?.gender)) || "-"}</Text>,
    },
    { label: t("Age"), value: <Text>{clientProfileData?.age}</Text> },
  ]
}

const HowToWinItem = ({ title, description }) => (
  <Flex gap="8px" align="flex-start" wrap={false}>
    <Icon icon="LuDiamond" size={12} style={{ color: theme["accent-color"], flexShrink: 0, marginTop: 4 }} />
    <Flex vertical gap={4} className="flexGrow">
      <Text strong className="fs14">
        {title}
      </Text>
      <Text className="fs14 gray-900">{description}</Text>
    </Flex>
  </Flex>
)

const ClientSummary = ({ clientId, clientDetails }) => {
  const { t } = useTranslation()
  const userPlatformKey = useSelector(state => state?.app?.user?.info?.AgencyConfiguration?.Platform?.platformKey)
  const clientFormRef = useRef()

  const {
    data: clientProfiling,
    isFetching: isLoadingProfiling,
    isError: hasProfilingError,
    error: profilingError,
    refetch: refetchProfiling,
  } = useGetSalesPlayBookClientProfilingQuery(clientId, {
    skip: !clientId,
  })

  const {
    data: clientSummary,
    isFetching: isLoadingSummary,
    isError: hasSummaryError,
    error: summaryError,
    refetch: refetchSummary,
  } = useGetSalesPlayBookClientSummaryQuery(clientId, {
    skip: !clientId,
  })

  const isLoading = isLoadingProfiling || isLoadingSummary
  const hasError = hasProfilingError || hasSummaryError
  const error = profilingError || summaryError

  const handleRetry = () => {
    refetchProfiling()
    refetchSummary()
  }

  const {
    personalityAssessment,
    hasPersonalityData,
    buyingPreferencesTableData,
    clientProfileTableData,
    nextSteps,
    personalityType,
    personalitySummary,
    winningMoves,
  } = useMemo(() => {
    const personality = clientSummary?.personalityAssessment || null
    return {
      personalityAssessment: personality,
      hasPersonalityData: personality !== null,
      buyingPreferencesTableData: transformBuyingPreferences(clientProfiling, t, userPlatformKey),
      clientProfileTableData: transformClientProfileData(clientDetails, t),
      nextSteps: clientSummary?.nextSteps?.length > 0 ? clientSummary.nextSteps : null,
      personalityType: localisedValue({ object: personality, key: "personalityType" }) || "",
      personalitySummary: localisedValue({ object: personality, key: "personalitySummary" }) || "",
      winningMoves: personality?.winningMoves?.length > 0 ? personality.winningMoves : [],
    }
  }, [clientSummary, clientProfiling, clientDetails, t, userPlatformKey])

  if (isLoading) {
    return (
      <Flex justify="center" align="center" className="w-100" style={{ minHeight: 300 }}>
        <Spinner size="large" tip={t("Loading client summary...")} />
      </Flex>
    )
  }

  if (hasError) {
    return (
      <div className="p-24 w-100">
        <ComponentState
          description={error?.message}
          status={error?.status}
          btnLoading={isLoading}
          onClick={handleRetry}
        />
      </div>
    )
  }

  return (
    <Group template="60% 1fr" gap="40px" className="w-100" style={{ alignItems: "start" }}>
      <Flex vertical gap="16px" className={styles.clientSummaryLeft}>
        <Card
          Shadow={false}
          variant="gradient"
          className={cx(styles.personalityCard, "text-start")}
          style={{ height: !hasPersonalityData && "400px" }}
        >
          <Flex vertical gap="12px" align="flex-start" className="w-100">
            <div>
              <Flex align="center" gap="6px" className="mbe-8">
                <Icon icon="GoLightBulb" size={18} style={{ color: theme["accent-color"] }} />
                <div>
                  <Title level={4} strong className="bold mbe-0">
                    {t("Personality Assessment")}
                    <Text className={styles.gradientText}>{personalityType}</Text>
                  </Title>
                </div>
              </Flex>
              <Text className="fs14 gray-900">{personalitySummary}</Text>
            </div>
            {winningMoves.length > 0 && (
              <Card
                Shadow={false}
                bordered={false}
                className={`${styles.howToWinCard} w-100`}
                style={{ "--ant-box-shadow-tertiary": "none" }}
              >
                <Flex vertical gap={16} align="flex-start">
                  <Text strong className="fs16 mbe-8">
                    {t("How the win the client")}
                  </Text>
                  <Flex vertical gap="16px">
                    {winningMoves.map((item, index) => {
                      const title = localisedValue({ object: item, key: "title" }) || ""
                      const description = localisedValue({ object: item, key: "description" }) || ""
                      return <HowToWinItem key={index} title={title} description={description} />
                    })}
                  </Flex>
                </Flex>
              </Card>
            )}
          </Flex>
          {!hasPersonalityData && (
            <div className={styles.personalityEmptyState}>
              <Flex vertical gap="8px" align="center" justify="center" className={styles.emptyStateContent}>
                <Icon icon="MdLockOutline" size={32} style={{ color: theme["base-color"] }} />
                <Text strong className="fs16" style={{ color: theme["gray-900"] }}>
                  {t("Unlock Personalized Client Insights")}
                </Text>
                <Text className="gray-800">
                  {t("Make the first call to discover personalized strategies and accelerate deal closure")}
                </Text>
              </Flex>
            </div>
          )}
        </Card>

        {nextSteps && (
          <Card
            Shadow={false}
            className={`${styles.howToWinCard} w-100`}
            style={{ "--ant-box-shadow-tertiary": "none" }}
          >
            <Flex vertical gap={16} align="flex-start">
              <Text strong className="fs16 mbe-8">
                {t("Next Steps")}
              </Text>
              <Flex vertical gap="16px">
                {nextSteps.map((item, index) => {
                  const title = localisedValue({ object: item, key: "title" }) || ""
                  const description = localisedValue({ object: item, key: "description" }) || ""
                  return <HowToWinItem key={index} title={title} description={description} />
                })}
              </Flex>
            </Flex>
          </Card>
        )}
      </Flex>
      <Flex style={{ position: "sticky", top: 20 }} vertical gap="16px">
        {buyingPreferencesTableData.length > 0 && (
          <InfoTable headerTitle={t("Buying Preferences")} tableData={buyingPreferencesTableData} />
        )}
        {clientProfileTableData.length > 0 && (
          <InfoTable headerTitle={t("Client Profile")} tableData={clientProfileTableData} headerIcon="FiUser" />
        )}
      </Flex>
    </Group>
  )
}

export default ClientSummary
